/**
 * Brand domain types, as the Admin API (`/api/admin/brands`) returns them.
 *
 * A "Brand" is the merchant whose vouchers Digital Codex sells — PlayStation,
 * Netflix. The catalogue is Brands → Regions (stores) → Denominations (the
 * sellable SKU). Terminology follows the backend (§49).
 *
 * States and reasons are names of the backend's shared rules
 * (UT system Services/Catalogue): what customers of a country can see
 * (visible) and actually buy (sellable), and why not. The UI labels them;
 * it never works them out itself.
 */

export interface BrandCategory {
  id: number;
  nameEn: string | null;
  nameAr: string | null;
  isActive: boolean;
}

/** Sellability of a brand's offered denominations in one country. */
export type BrandSellability = 'All' | 'Some' | 'None';

export interface BrandCountrySummary {
  countryId: number;
  offered: number;
  visible: number;
  sellable: number;
  sellability: BrandSellability;
}

/** One row of the brand list. */
export interface BrandListItem {
  id: number;
  name: string;
  arName: string | null;
  isActive: boolean;
  category: BrandCategory;
  imageUrl: string | null;
  sortOrder: number | null;
  denominationCount: number;
  activeDenominationCount: number;
  regionCount: number;
  /** For the requested country; null when none was requested. */
  countrySummary: BrandCountrySummary | null;
  /** E.g. NoActiveDenominations, DenominationsWithoutProvider, MissingArabicName. */
  attention: string[];
}

/**
 * How Brand Details lists denominations — always within each region (regions
 * stay in store order), sorted by the Admin API. The default (storefront
 * position, ascending) is the storefront order itself.
 */
export interface DenominationSort {
  /** `sortOrder` (storefront position) or `name`. */
  sort: 'sortOrder' | 'name';
  direction: 'asc' | 'desc';
}

export const DEFAULT_DENOMINATION_SORT: DenominationSort = { sort: 'sortOrder', direction: 'asc' };

/** The same default for the URL state (`?sort=…&direction=…`) shared by the page and the table. */
export const DENOMINATION_SORT_DEFAULTS = {
  defaultSort: 'sortOrder',
  defaultDirection: 'asc',
} as const;

export interface BrandListParams {
  page: number;
  pageSize: number;
  search?: string;
  isActive?: boolean;
  categoryId?: number;
  /** Country whose visibility/sellability the list summarises. */
  countryId?: number;
  /** Server-side sorts only: name, position (sortOrder), denominations. */
  sort?: string;
  direction?: 'asc' | 'desc';
}

// ------------------------------------------------------------ Brand Details

export interface BrandHeader {
  id: number;
  name: string;
  arName: string | null;
  isActive: boolean;
  category: BrandCategory;
  imageUrl: string | null;
  description: string | null;
  slug: string | null;
  sortOrder: number | null;
}

export interface CatalogueCountry {
  id: number;
  code: string | null;
  nameEn: string | null;
  nameAr: string | null;
  isActive: boolean;
  hasActivePricing: boolean;
  /** The country's storefront address; null when not set. */
  siteUrl?: string | null;
}

export interface BrandRegion {
  id: number;
  name: string | null;
  arName: string | null;
  code: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
  denominationCount: number;
}

export type CostSource = 'Valid' | 'NoProvider' | 'Blocked';

export interface ChosenProvider {
  vendorId: number;
  vendorName: string | null;
  /** Absent for staff without catalogue.cost.read. */
  cost?: number;
  costCurrency?: string;
  /** Next cheapest eligible cost, in the chosen provider's currency. */
  nextLowestCost?: number;
}

export interface DenominationCountryState {
  countryId: number;
  offered: boolean;
  visible: boolean;
  visibilityReasons: string[];
  sellable: boolean;
  sellabilityReasons: string[];
  costSource: CostSource;
  chosenProvider: ChosenProvider | null;
  /**
   * Mapped and switched on, yet unable to supply in this country (no product id,
   * balance too low, no exchange rate...). Mappings switched off on purpose are
   * not listed. With a chosen provider these are broken fallbacks.
   */
  unusableProviders: UnusableProvider[];
}

export interface UnusableProvider {
  vendorId: number;
  vendorName: string | null;
  /** A provider eligibility reason code, e.g. ProductIdentifierMissing; name it with reasonLabel(). */
  reason: string;
}

export interface ProviderMapping {
  mappingId: number;
  vendorId: number;
  vendorName: string | null;
  productIdentifier: string | null;
  isActive: boolean;
  vendorIsActive: boolean;
  /** Absent for staff without catalogue.cost.read. */
  cost?: number;
  costCurrency?: string;
}

export interface BrandDenomination {
  id: number;
  name: string;
  arName: string | null;
  currencyCode: string | null;
  /** Sold at least once: region and currency are then fixed. */
  hasSales: boolean;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  regionId: number;
  countries: DenominationCountryState[];
  mappings: ProviderMapping[];
  hasZeroCostMapping: boolean;
}

export interface AttentionItem {
  code: string;
  count: number;
  countryId: number | null;
}

export interface BrandDetails {
  brand: BrandHeader;
  countries: CatalogueCountry[];
  regions: BrandRegion[];
  /** Every denomination, active and inactive, in storefront order. */
  denominations: BrandDenomination[];
  attention: AttentionItem[];
}

// ----------------------------------------------------------------- changes

export type CatalogueChange =
  | 'activateBrand'
  | 'deactivateBrand'
  | 'activateDenominations'
  | 'deactivateDenominations'
  | 'offer'
  | 'withdraw';

export interface CatalogueImpactRequest {
  change: CatalogueChange;
  denominationIds?: number[];
  countryId?: number;
}

export interface CountryImpact {
  countryId: number;
  becomeHidden: number;
  becomeVisible: number;
  becomeSellable: number;
}

export interface CatalogueImpact {
  change: CatalogueChange;
  affectedCount: number;
  countries: CountryImpact[];
  blocked: { denominationId: number; reason: string }[];
  warnings: string[];
}

/**
 * PUT /api/admin/brands/{brandId}/denominations/{id}. Status and availability
 * change separately; the position only through the region's denomination order
 * (a new one goes after the last one of its region).
 */
export interface DenominationPayload {
  name: string;
  arName: string | null;
  regionId: number;
  currencyCode: string;
  imageUrl: string | null;
}

/** POST /api/admin/brands/{brandId}/denominations. Starts inactive unless requested. */
export interface CreateDenominationPayload extends DenominationPayload {
  isActive: boolean;
}

/**
 * Payload for creating a brand (POST /api/admin/brands). A new brand goes after
 * the last one; positions change only through the storefront brand order.
 */
export interface CreateBrandPayload {
  name: string;
  arName: string;
  categoryId: number;
  slug: string | null;
  description: string | null;
  imageUrl: string | null;
  /** New brands start inactive unless requested, so they can be prepared first. */
  isActive: boolean;
}

/** Payload for editing a brand (PUT /api/admin/brands/{id}). Status is changed separately. */
export type UpdateBrandPayload = Omit<CreateBrandPayload, 'isActive'>;

/**
 * What still points at a denomination and blocks deleting it. Customer, order
 * and payment records only — configuration (country offerings, provider
 * mappings) is deleted with it.
 */
export type CatalogueUsageSource =
  | 'orders'
  | 'carts'
  | 'checkouts'
  | 'favourites'
  | 'partnerProfits'
  | 'shares';

export interface CatalogueUsage {
  source: CatalogueUsageSource;
  count: number;
}

/** GET /api/admin/brands/{id}/deletion-check. */
export interface CatalogueDeletionCheck {
  /** True when no denomination of the brand is used (a brand without denominations too). */
  canDeleteBrand: boolean;
  denominationCount: number;
  usedDenominationCount: number;
  denominations: { denominationId: number; canDelete: boolean; usedBy: CatalogueUsage[] }[];
}

// ---------------------------------------------------------- storefront order

/**
 * A brand in the storefront brand order (GET /api/admin/brands/order). The
 * array order IS the display order; `sortOrder` is informational only and is
 * never used to sort in the UI.
 */
export interface BrandOrderItem {
  id: number;
  name: string;
  arName: string | null;
  isActive: boolean;
  imageUrl: string | null;
  sortOrder: number;
  categoryId: number;
  categoryNameEn: string | null;
}

/**
 * A denomination in its brand and region's storefront order
 * (GET /api/admin/brands/{id}/denominations/order). The array order IS the
 * display order; `sortOrder` is informational only.
 */
export interface DenominationOrderItem {
  id: number;
  name: string;
  arName: string | null;
  currencyCode: string | null;
  isActive: boolean;
  sortOrder: number;
  regionId: number;
}
